import type { ISODate } from "./types.js";

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 86_400_000;

export function isISODate(value: unknown): value is ISODate {
  if (typeof value !== "string" || !ISO_DATE_RE.test(value)) return false;
  const ms = Date.parse(`${value}T00:00:00Z`);
  return !Number.isNaN(ms) && toISO(ms) === value;
}

export function assertISODate(value: unknown, label = "date"): ISODate {
  if (!isISODate(value)) {
    throw new Error(`Invalid ${label}: expected YYYY-MM-DD, got ${JSON.stringify(value)}`);
  }
  return value;
}

/** Milliseconds at UTC midnight for the given date. */
export function parseISO(date: ISODate): number {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  return Date.UTC(y, m - 1, d);
}

export function toISO(ms: number): ISODate {
  const d = new Date(ms);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function addDays(date: ISODate, days: number): ISODate {
  return toISO(parseISO(date) + days * DAY_MS);
}

/** 0 = Sunday … 6 = Saturday. */
export function dayOfWeek(date: ISODate): number {
  return new Date(parseISO(date)).getUTCDay();
}

export function compareISO(a: ISODate, b: ISODate): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** `to - from` in calendar days (negative if `to` is earlier). */
export function calendarDaysBetween(from: ISODate, to: ISODate): number {
  return Math.round((parseISO(to) - parseISO(from)) / DAY_MS);
}

export function assertWorkingDays(workingDays: readonly number[]): void {
  if (workingDays.length === 0) {
    throw new Error("Settings must include at least one working day");
  }
  for (const d of workingDays) {
    if (!Number.isInteger(d) || d < 0 || d > 6) {
      throw new Error(`Invalid working day ${d}: expected 0 (Sunday) to 6 (Saturday)`);
    }
  }
}

export function isWorkingDay(date: ISODate, workingDays: readonly number[]): boolean {
  return workingDays.includes(dayOfWeek(date));
}

/** The last working day strictly before `date`. */
export function previousWorkingDay(date: ISODate, workingDays: readonly number[]): ISODate {
  assertWorkingDays(workingDays);
  let d = addDays(date, -1);
  while (!isWorkingDay(d, workingDays)) d = addDays(d, -1);
  return d;
}

/** The first working day strictly after `date`. */
export function nextWorkingDay(date: ISODate, workingDays: readonly number[]): ISODate {
  assertWorkingDays(workingDays);
  let d = addDays(date, 1);
  while (!isWorkingDay(d, workingDays)) d = addDays(d, 1);
  return d;
}

/** `date` itself if it is a working day, otherwise the last working day before it. */
export function rollBackToWorkingDay(date: ISODate, workingDays: readonly number[]): ISODate {
  return isWorkingDay(date, workingDays) ? date : previousWorkingDay(date, workingDays);
}

/** Move `n` working days backwards from `date`. `n = 0` returns `date` unchanged. */
export function subtractWorkingDays(date: ISODate, n: number, workingDays: readonly number[]): ISODate {
  if (!Number.isInteger(n) || n < 0) throw new Error(`Working-day count must be a non-negative integer, got ${n}`);
  let d = date;
  for (let i = 0; i < n; i++) d = previousWorkingDay(d, workingDays);
  return d;
}

/**
 * Signed count of working days between two dates.
 * Positive: working days in (from, to]. Negative: minus the working days in (to, from].
 */
export function workingDaysBetween(from: ISODate, to: ISODate, workingDays: readonly number[]): number {
  const sign = compareISO(from, to);
  if (sign === 0) return 0;
  const [lo, hi] = sign < 0 ? [from, to] : [to, from];
  let count = 0;
  for (let d = addDays(lo, 1); compareISO(d, hi) <= 0; d = addDays(d, 1)) {
    if (isWorkingDay(d, workingDays)) count++;
  }
  return sign < 0 ? count : -count;
}

/** Every working day in [from, to], in order. */
export function workingDaysIn(from: ISODate, to: ISODate, workingDays: readonly number[]): ISODate[] {
  const out: ISODate[] = [];
  for (let d = from; compareISO(d, to) <= 0; d = addDays(d, 1)) {
    if (isWorkingDay(d, workingDays)) out.push(d);
  }
  return out;
}

/** The Monday on or before `date`. Weeks are labelled by it ("W/C 26 Oct"). */
export function startOfWeek(date: ISODate): ISODate {
  return addDays(date, -((dayOfWeek(date) + 6) % 7));
}

/** Today's calendar date in the given IANA timezone. */
export function todayInTimezone(timeZone: string, now: Date = new Date()): ISODate {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}
